"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import { formatCurrency } from "@/lib/utils";
import { useTranslations } from "@/hooks/useTranslations";

type QuickQuoteFormData = {
  clientId: string;
  templateId?: string;
  customPrice?: string;
  sendImmediately: boolean;
};

interface Client {
  id: string;
  name: string;
}

interface Template {
  id: string;
  name: string;
  description: string | null;
  lineItems: Array<{
    description: string;
    quantity: number;
    unitPrice: number;
    type: "material" | "labor";
  }>;
}

interface QuickQuoteModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function QuickQuoteModal({ isOpen, onClose }: QuickQuoteModalProps) {
  const router = useRouter();
  const { toast } = useToast();
  const t = useTranslations();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);

  const quickQuoteSchema = z.object({
    clientId: z.string().min(1, t("quickActions.quote.clientRequired")),
    templateId: z.string().optional(),
    customPrice: z.string().optional(),
    sendImmediately: z.boolean(),
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<QuickQuoteFormData>({
    resolver: zodResolver(quickQuoteSchema),
    defaultValues: {
      clientId: "",
      templateId: "",
      customPrice: "",
      sendImmediately: false,
    },
  });

  const clientId = watch("clientId");
  const templateId = watch("templateId");
  const sendImmediately = watch("sendImmediately");

  // Fetch clients and templates when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsLoadingData(true);
      Promise.all([
        fetch("/api/clients").then((res) => {
          if (!res.ok) throw new Error("Failed to fetch clients");
          return res.json();
        }),
        fetch("/api/quotes/templates").then((res) => {
          if (!res.ok) throw new Error("Failed to fetch templates");
          return res.json();
        }),
      ])
        .then(([clientsResult, templatesData]) => {
          const clientsData = clientsResult.data || clientsResult; // Handle both new and old format
          setClients(clientsData);
          setTemplates(templatesData);
        })
        .catch((error) => {
          console.error("Error loading data:", error);
          toast({
            title: t("quickActions.quote.toast.error"),
            description: t("quickActions.quote.toast.loadError"),
            variant: "destructive",
          });
        })
        .finally(() => {
          setIsLoadingData(false);
        });
    }
  }, [isOpen, toast, t]);

  // Update selected template when templateId changes
  useEffect(() => {
    if (templateId) {
      const template = templates.find((t) => t.id === templateId);
      setSelectedTemplate(template || null);
    } else {
      setSelectedTemplate(null);
    }
  }, [templateId, templates]);

  const calculateTemplateTotal = (template: Template) => {
    return template.lineItems.reduce(
      (sum, item) => sum + item.quantity * item.unitPrice,
      0
    );
  };

  const onSubmit = async (data: QuickQuoteFormData) => {
    setIsSubmitting(true);

    try {
      let lineItems: Array<{
        id: string;
        description: string;
        quantity: number;
        unitPrice: number;
        type: "material" | "labor";
        total: number;
      }> = [];

      if (selectedTemplate) {
        // Use template line items
        lineItems = selectedTemplate.lineItems.map((item) => ({
          id: crypto.randomUUID(),
          description: item.description,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          type: item.type,
          total: item.quantity * item.unitPrice,
        }));
      } else if (data.customPrice) {
        // Create a single custom line item
        const price = parseFloat(data.customPrice);
        lineItems = [
          {
            id: crypto.randomUUID(),
            description: t("quickActions.quote.serviceItem"),
            quantity: 1,
            unitPrice: price,
            type: "labor",
            total: price,
          },
        ];
      } else {
        throw new Error(t("quickActions.quote.selectTemplateOrPrice"));
      }

      const subtotal = lineItems.reduce((sum, item) => sum + item.total, 0);
      const tax = subtotal * 0.1; // 10% tax
      const total = subtotal + tax;

      // Calculate valid until date (30 days from now)
      const validUntil = new Date();
      validUntil.setDate(validUntil.getDate() + 30);

      const response = await fetch("/api/quotes", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          clientId: data.clientId,
          lineItems,
          subtotal,
          tax,
          total,
          validUntil: validUntil.toISOString().split("T")[0],
          status: "DRAFT",
          notes: "",
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to create quote");
      }

      const quote = await response.json();

      // If sending immediately, call the send endpoint
      if (data.sendImmediately) {
        try {
          const sendResponse = await fetch(`/api/quotes/${quote.id}/send`, {
            method: "POST",
          });

          if (!sendResponse.ok) {
            throw new Error("Failed to send quote");
          }

          const sendResult = await sendResponse.json();

          toast({
            title: t("quickActions.quote.toast.sent"),
            description: sendResult.emailSent
              ? t("quickActions.quote.toast.sentDescription")
              : t("quickActions.quote.toast.sentWarning"),
          });
        } catch (sendError) {
          console.error("Error sending quote:", sendError);
          toast({
            title: t("quickActions.quote.toast.created"),
            description: t("quickActions.quote.toast.createdPartial"),
          });
        }
      } else {
        toast({
          title: t("quickActions.quote.toast.created"),
          description: t("quickActions.quote.toast.createdDescription"),
        });
      }

      reset();
      onClose();
      router.push(`/quotes/${quote.id}`);
      router.refresh();
    } catch (error) {
      console.error("Error creating quote:", error);
      toast({
        title: t("quickActions.quote.toast.error"),
        description:
          error instanceof Error ? error.message : t("quickActions.quote.toast.errorDescription"),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      reset();
      setSelectedTemplate(null);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t("quickActions.quote.title")}</DialogTitle>
          <DialogDescription>
            {t("quickActions.quote.description")}
          </DialogDescription>
        </DialogHeader>

        {isLoadingData ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="clientId">
                {t("quickActions.quote.clientLabel")} <span className="text-error">*</span>
              </Label>
              <Select
                value={clientId}
                onValueChange={(value) => setValue("clientId", value)}
                disabled={isSubmitting}
              >
                <SelectTrigger id="clientId">
                  <SelectValue placeholder={t("quickActions.quote.selectClient")} />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.clientId && (
                <p className="text-sm text-error">{errors.clientId.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="templateId">{t("quickActions.quote.templateLabel")}</Label>
              <Select
                value={templateId}
                onValueChange={(value) => setValue("templateId", value)}
                disabled={isSubmitting}
              >
                <SelectTrigger id="templateId">
                  <SelectValue placeholder={t("quickActions.quote.templatePlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name}
                      {template.description && ` - ${template.description}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedTemplate && (
              <div className="rounded-md border p-3 space-y-2 bg-muted/50">
                <p className="text-sm font-medium">{t("quickActions.quote.templatePreview")}</p>
                <div className="text-sm space-y-1">
                  <p className="text-muted-foreground">
                    {t("quickActions.quote.lineItems", { count: selectedTemplate.lineItems.length })}
                  </p>
                  <p className="font-semibold">
                    {t("quickActions.quote.estimatedTotal")} {formatCurrency(calculateTemplateTotal(selectedTemplate))}
                  </p>
                </div>
              </div>
            )}

            {!templateId && (
              <div className="space-y-2">
                <Label htmlFor="customPrice">{t("quickActions.quote.customPriceLabel")}</Label>
                <Input
                  id="customPrice"
                  type="number"
                  step="0.01"
                  {...register("customPrice")}
                  placeholder="0.00"
                  disabled={isSubmitting}
                />
              </div>
            )}

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="sendImmediately"
                checked={sendImmediately}
                onChange={(e) => setValue("sendImmediately", e.target.checked)}
                disabled={isSubmitting}
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="sendImmediately" className="text-sm font-normal cursor-pointer">
                {t("quickActions.quote.sendImmediately")}
              </Label>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t("quickActions.quote.creating")}
                  </>
                ) : sendImmediately ? (
                  t("quickActions.quote.createAndSendButton")
                ) : (
                  t("quickActions.quote.createButton")
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
